import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

const PRODUCT_LINE_LICENSE_MAP = {
  'RISET': 'Cyber Security Platform License',
  'PENT TEST': 'Penetration Testing Platform License',
  'TEST BENCH': 'Test Bench Platform License',
};

const STATUS_PILL = {
  pending: 'bg-[rgba(245,158,11,0.1)] text-[#f59e0b]',
  active: 'bg-[rgba(16,185,129,0.1)] text-[#10b981]',
  issued: 'bg-[rgba(6,182,212,0.12)] text-[#06b6d4]',
  registered: 'bg-[rgba(245,158,11,0.1)] text-[#f59e0b]',
  activated: 'bg-[rgba(16,185,129,0.1)] text-[#10b981]',
};

function StatusPill({ status }) {
  const label = status === 'registered' ? 'Pending' : status.charAt(0).toUpperCase() + status.slice(1);
  const cls = STATUS_PILL[status] || STATUS_PILL.pending;
  return (
    <span className={`inline-flex items-center gap-[4px] text-[10px] font-semibold px-2 py-[2px] rounded-full ${cls}`}>
      <span className="w-[5px] h-[5px] rounded-full bg-current" />
      {label}
    </span>
  );
}

function LockIcon() {
  return (
    <svg className="w-[14px] h-[14px] opacity-50 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openBlocks, setOpenBlocks] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ unitName: '', serialNumber: '' });
  const [adding, setAdding] = useState(false);
  const [activating, setActivating] = useState(null);

  const load = async () => {
    try {
      const { data: res } = await api.get(`/admin/product-lines/${id}`);
      setData(res);
      // Open first appliance by default
      if (res.productLine?.appliances?.length > 0) {
        setOpenBlocks({ [res.productLine.appliances[0].id]: true });
      }
    } catch (err) {
      console.error('Load product detail error:', err);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const toggleBlock = (appId) => {
    setOpenBlocks(prev => ({ ...prev, [appId]: !prev[appId] }));
  };

  const pl = data?.productLine;
  const appliances = pl?.appliances || [];

  // Filter by tab
  const filtered = activeTab === 'all'
    ? appliances
    : activeTab === 'pending'
      ? appliances.filter(a => a.status === 'registered' || a.status === 'pending_activation')
      : appliances.filter(a => a.status === 'activated' || a.status === 'online' || a.status === 'offline');

  const pendingCount = appliances.filter(a => a.status === 'registered' || a.status === 'pending_activation').length;
  const activatedCount = appliances.filter(a => a.status === 'activated' || a.status === 'online' || a.status === 'offline').length;
  const totalLicenses = appliances.reduce((s, a) => s + (a.licenses?.length || 0), 0);

  // Next AIBOX number
  const nextNum = appliances.length + 1;

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const name = addForm.unitName || `AI BOX #${nextNum}`;
      const serial = addForm.serialNumber || `SN-${pl.name.replace(/\s+/g, '')}-${String(nextNum).padStart(3, '0')}`;

      await api.post('/admin/appliances', {
        unitName: name,
        serialNumber: serial,
        organizationId: pl.organization.id,
        productLineId: pl.id,
      });
      toast.success('AIBOX registered with 3 license keys');
      setShowAddModal(false);
      setAddForm({ unitName: '', serialNumber: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to register');
    } finally {
      setAdding(false);
    }
  };

  const handleBatchCert = async (regenerate = false) => {
    try {
      const { data: res } = await api.post('/certificates/batch', {
        productLineId: pl.id,
        organizationId: pl.organization.id,
        regenerate,
      });

      if (res.alreadyGenerated) {
        // Show confirmation to regenerate
        if (window.confirm(`${res.existingCount} of ${res.totalCount} appliances already have certificates.\n\nRegenerate all certificate PDFs?`)) {
          handleBatchCert(true);
        }
        return;
      }

      toast.success(`${res.count} certificate(s) generated with ${res.count * 3} license pages`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const handleActivate = async (appliance) => {
    if (!window.confirm(`Activate ${appliance.unitName} — ${pl.name}?\n\nThis will activate all ${appliance.licenses?.length || 3} licenses.`)) return;
    setActivating(appliance.id);
    try {
      await api.post('/activate/confirm', { applianceId: appliance.id });
      toast.success(`${appliance.unitName} activated — all licenses are now active`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Activation failed');
    } finally {
      setActivating(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!pl) {
    return <div className="text-center py-20 text-[#64748b]">Product line not found</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-0">
        {/* Breadcrumb */}
        <div className="text-[11px] text-[#475569] mb-[2px] flex items-center gap-[6px]">
          <a onClick={() => navigate('/admin/products')} className="text-[#06b6d4] cursor-pointer hover:underline">Products</a>
          <span>/</span>
          <span>{pl.name}</span>
        </div>
        <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">{pl.name} — {pl.fullName}</h2>
        <p className="text-[13px] text-[#64748b] mt-[2px]">
          {pl.organization?.shortName || pl.organization?.name} — {appliances.length} AIBOX units, {totalLicenses} licenses
        </p>
        <div className="flex gap-2 mt-3">
          <Button onClick={() => { setAddForm({ unitName: `AI BOX #${nextNum}`, serialNumber: '' }); setShowAddModal(true); }}>
            <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add AIBOX
          </Button>
          <Button variant="ghost" onClick={handleBatchCert}>
            <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Generate Certificate
          </Button>
          <Button variant="ghost">
            <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </Button>
        </div>
      </div>

      <div className="mt-5">
        {/* Tabs */}
        <div className="flex gap-[2px] border-b border-[rgba(148,163,184,0.06)] mb-5">
          {[
            { key: 'all', label: `All Appliances (${appliances.length})` },
            { key: 'pending', label: `Pending (${pendingCount})` },
            { key: 'activated', label: `Activated (${activatedCount})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-[10px] text-[12.5px] font-medium border-b-2 -mb-px transition-all ${
                activeTab === tab.key
                  ? 'text-[#06b6d4] border-[#06b6d4] font-semibold'
                  : 'text-[#475569] border-transparent hover:text-[#94a3b8]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Appliance list */}
        {filtered.length === 0 ? (
          <div className="text-center py-10">
            <svg className="w-10 h-10 mx-auto mb-2 opacity-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1}>
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
            </svg>
            <h3 className="text-[15px] font-semibold text-[#64748b] mb-1">No appliances</h3>
            <p className="text-[12px] text-[#475569]">Click "Add AIBOX" to register a new unit</p>
          </div>
        ) : (
          <div className="flex flex-col gap-[10px]">
            {filtered.map((appliance) => {
              const isOpen = !!openBlocks[appliance.id];
              const appStatus = appliance.status === 'activated' || appliance.status === 'online' || appliance.status === 'offline' ? 'activated' : 'registered';

              return (
                <div key={appliance.id} className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] overflow-hidden">
                  {/* Header — always visible */}
                  <div
                    className="flex items-center justify-between px-[18px] py-[14px] cursor-pointer transition-colors hover:bg-[rgba(148,163,184,0.03)]"
                    onClick={() => toggleBlock(appliance.id)}
                  >
                    <h4 className="text-[14px] font-semibold text-[#e2e8f0] flex items-center gap-2">
                      {appliance.unitName}
                      <span className="font-mono text-[11px] text-[#475569] font-normal">— {pl.name}</span>
                    </h4>
                    <div className="flex items-center gap-[10px]">
                      <StatusPill status={appStatus} />
                      {appStatus === 'registered' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleActivate(appliance); }}
                          disabled={activating === appliance.id}
                          className="text-[10.5px] font-semibold px-[10px] py-[3px] rounded-[5px] border border-[rgba(6,182,212,0.25)] text-[#06b6d4] hover:bg-[rgba(6,182,212,0.1)] transition-all disabled:opacity-50"
                        >
                          {activating === appliance.id ? 'Activating...' : 'Activate'}
                        </button>
                      )}
                      <svg
                        className={`w-[18px] h-[18px] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>
                  </div>

                  {/* Expanded: license rows */}
                  {isOpen && (
                    <div className="border-t border-[rgba(148,163,184,0.06)] px-[18px]">
                      {appliance.licenses && appliance.licenses.length > 0 ? (
                        appliance.licenses.map((lic, i) => (
                          <div
                            key={lic.id}
                            className={`grid items-center py-[10px] text-[12.5px] ${
                              i < appliance.licenses.length - 1 ? 'border-b border-[rgba(148,163,184,0.06)]' : ''
                            }`}
                            style={{ gridTemplateColumns: '1fr 220px 100px' }}
                          >
                            <span className="text-[#94a3b8] font-[450] flex items-center gap-[6px]">
                              <LockIcon />
                              {lic.softwareName}
                            </span>
                            <span className="font-mono text-[11.5px] text-[#06b6d4] tracking-[0.3px]">
                              {lic.licenseKey}
                            </span>
                            <span className="text-right">
                              <StatusPill status={lic.status} />
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="py-4 text-[12px] text-[#475569]">No licenses assigned</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add AIBOX Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add AIBOX">
        <p className="text-[12.5px] text-[#64748b] mb-4">
          Register a new AIBOX unit to {pl.name}. Three license keys will be auto-generated.
        </p>
        <form onSubmit={handleAdd} className="space-y-4">
          <Input
            label="Unit Name"
            value={addForm.unitName}
            onChange={(e) => setAddForm({ ...addForm, unitName: e.target.value })}
            placeholder={`AI BOX #${nextNum}`}
          />
          <Input
            label="Serial Number"
            value={addForm.serialNumber}
            onChange={(e) => setAddForm({ ...addForm, serialNumber: e.target.value })}
            placeholder="Leave empty to auto-generate"
          />
          <div className="bg-[#060a10] rounded-lg p-3 mt-3">
            <p className="text-[10px] text-[#475569] uppercase tracking-wider font-semibold mb-2">Auto-Generated Licenses</p>
            <div className="space-y-1 text-[12px] text-[#94a3b8]">
              <div className="flex items-center gap-2"><LockIcon /> AI Core License</div>
              <div className="flex items-center gap-2"><LockIcon /> AI Agentic License</div>
              <div className="flex items-center gap-2"><LockIcon /> {PRODUCT_LINE_LICENSE_MAP[pl.name.toUpperCase()] || `${pl.name} Platform License`}</div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button type="submit" disabled={adding}>{adding ? 'Registering...' : 'Register AIBOX'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
